import React, { useContext, useEffect, useState } from 'react';

import { ServiceResponse } from '../../app/services/shared/interfaces';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../components/default/DefaultCard';

import DefaultModal from '../../components/default/DefaultModal';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';

import { toast } from 'react-toastify';

import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import { AppContext } from '../../contexts/AppContext';
import useBankAccountTypeService from '../../app/services/hooks/useBankAccountType';
import { BankAccountType, BankAccountTypeFilters } from '../../app/models/BankAccountType';
import BankAccountTypeDataTable from './components/tables/BankAccountTypeDataTable';
import BankAccountTypeCreate from './components/BankAccountTypeCreate';
import BankAccountTypeEdit from './components/BankAccountTypeEdit';
import { useHistory } from 'react-router-dom';
import useNavigationPage from '../../hooks/useNavigationPage';

const breadcrumbs: BreadcrumbsItem[] = [
    {
        name: 'Tipo de cuentas bancarias',
        url: '/bank-account-types',
        isActive: true
    }
];

const BankAccountTypes = () => {
    const history = useHistory();
    const { showLoading, hideLoading, changeAnimation } = useContext(AppContext);
    const {
        fetchingGetBankAccountTypes,
        getBankAccountTypes,
        deleteBankAccountType,
        activeBankAccountType
    } = useBankAccountTypeService();
    const [bankAccountTypes, setBankAccountTypes] = useState<BankAccountType[]>([]);
    const { navigationPage } = useNavigationPage();

    const [bankAccountTypeIdEdit, setBankAccountTypeIdEdit] = useState<number>(-1);
    const [showingEdit, setShowingEdit] = useState(false);
    const [showingCreate, setShowingCreate] = useState(false);
    const [totalRows, setTotalRows] = useState<number>(0);

    const [bankAccountTypeFilter, setBankAccountTypeFilter] = useState<BankAccountTypeFilters>({
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'desc'
    });

    useEffect(() => {
        reloadTable();
    }, [bankAccountTypeFilter]);

    const getAllBankAccountTypes = () => {
        getBankAccountTypes(bankAccountTypeFilter, {
            onSuccess: (response: ServiceResponse) => {
                setBankAccountTypes(response.data.bank_account_types);
                setTotalRows(response.data.total_rows);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
                if (response.data.to_dashboard) {
                    navigationPage('/');
                }
            }
        });
    };

    const showEdit = (bankAccountTypeId: number) => {
        setShowingEdit(true);
        setBankAccountTypeIdEdit(bankAccountTypeId);
    };

    const hideEdit = () => {
        setShowingEdit(false);
        setBankAccountTypeIdEdit(-1);
    };

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const reloadTable = () => {
        getAllBankAccountTypes();
    };

    const destroy = (BankAccountTypeId: number) => {
        const _text = 'Está a punto de eliminar el tipo de cuenta #' + BankAccountTypeId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Si, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando tipo de cuenta...');
                        deleteBankAccountType(BankAccountTypeId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });

                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'La BankAccountType no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    const active = (BankAccountTypeId: number, active: boolean) => {
        activeBankAccountType(BankAccountTypeId, active, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().successAlert({
                    title: 'Éxito',
                    message: response.message
                });

                reloadTable();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
            }
        });
    };

    return (
        <>
            <Breadcrumbs pageSection="Tipo de cuentas bancarias" breadcrumbs={breadcrumbs} />

            <DefaultCard>
                <DefaultToolBar
                    left={<ButtonCreate callbackCreate={showCreate} title="Nuevo tipo de cuenta" />}
                />
                <BankAccountTypeDataTable
                    bankAccountTypes={bankAccountTypes}
                    edit={showEdit}
                    destroy={destroy}
                    active={active}
                    loading={fetchingGetBankAccountTypes}
                    filter={bankAccountTypeFilter}
                    setFilter={setBankAccountTypeFilter}
                    totalRows={totalRows}
                    callbackSuccess={reloadTable}
                />
            </DefaultCard>

            {showingCreate ? (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear Tipo de cuenta"
                    backdrop={true}
                    showFooter={false}
                >
                    <BankAccountTypeCreate
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                        onError={hideCreate}
                    />
                </DefaultModal>
            ) : null}

            {showingEdit ? (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar Tipo de cuenta"
                    backdrop={true}
                    showFooter={false}
                >
                    <BankAccountTypeEdit
                        bankAccountTypeId={bankAccountTypeIdEdit}
                        onSaved={() => {
                            reloadTable();
                            hideEdit();
                        }}
                        onCancel={hideEdit}
                        onError={hideEdit}
                    />
                </DefaultModal>
            ) : null}
        </>
    );
};

export default BankAccountTypes;
